export const TOUR_MAX_STOPS = 100;
export const TOUR_TRANSITION_SPEEDS = ["quick", "moderate", "relaxed", "slow", "very_slow"];
export const DEFAULT_TOUR_TRANSITION_SPEED = "relaxed";

export function normalizeTourTransitionSpeed(value) {
  return TOUR_TRANSITION_SPEEDS.includes(value) ? value : DEFAULT_TOUR_TRANSITION_SPEED;
}

export function tourTransitionDuration(value) {
  return { quick: 420, moderate: 650, relaxed: 900, slow: 1300, very_slow: 1800 }[normalizeTourTransitionSpeed(value)];
}

export function normalizeViewport(value) {
  if (!value || typeof value !== "object") return null;
  const x = Number(value.x);
  const y = Number(value.y);
  const zoom = Number(value.zoom);
  if (![x, y, zoom].every(Number.isFinite) || zoom <= 0 || zoom > 20) return null;
  return { x, y, zoom };
}

export function normalizeViewportSize(value) {
  if (!value || typeof value !== "object") return null;
  const width = Number(value.width);
  const height = Number(value.height);
  if (![width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
  return { width, height };
}

export function viewportForSize(viewport, savedSize, currentSize) {
  const normalized = normalizeViewport(viewport);
  const source = normalizeViewportSize(savedSize);
  const current = normalizeViewportSize(currentSize);
  if (!normalized || !source || !current) return normalized;
  return {
    ...normalized,
    x: normalized.x + (current.width - source.width) / 2,
    y: normalized.y + (current.height - source.height) / 2,
  };
}

export function normalizeTour(value) {
  const source = value && typeof value === "object" ? value : {};
  const rawStops = Array.isArray(source.stops) ? source.stops : [];
  const seen = new Set();
  const stops = [];
  for (const raw of rawStops.slice(0, TOUR_MAX_STOPS)) {
    if (!raw || typeof raw !== "object") continue;
    const id = String(raw.id || "").trim().slice(0, 160);
    const viewport = normalizeViewport(raw.viewport);
    if (!id || seen.has(id) || !viewport) continue;
    seen.add(id);
    const label = String(raw.label || "").trim().slice(0, 120);
    const viewportSize = normalizeViewportSize(raw.viewport_size);
    stops.push({ id, ...(label ? { label } : {}), viewport, ...(viewportSize ? { viewport_size: viewportSize } : {}) });
  }
  return { stops, transition_speed: normalizeTourTransitionSpeed(source.transition_speed) };
}

export function isEditableEventTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true'], [role='textbox']"));
}

export function addTourStop(tour, stop) {
  return normalizeTour({ stops: [...normalizeTour(tour).stops, stop] }).stops;
}

export function updateTourStop(tour, index, viewport, viewportSize = null) {
  return normalizeTour({ stops: normalizeTour(tour).stops.map((stop, stopIndex) => (
    stopIndex === index ? { ...stop, viewport, ...(viewportSize ? { viewport_size: viewportSize } : {}) } : stop
  )) }).stops;
}

export function updateTourStopLabel(tour, index, label) {
  const normalizedLabel = String(label || "").trim().slice(0, 120);
  return normalizeTour({ stops: normalizeTour(tour).stops.map((stop, stopIndex) => (
    stopIndex === index ? { ...stop, label: normalizedLabel } : stop
  )) }).stops;
}

export function duplicateTourStop(tour, index, id) {
  const stops = [...normalizeTour(tour).stops];
  if (!stops[index] || !id || stops.length >= TOUR_MAX_STOPS) return stops;
  stops.splice(index + 1, 0, { ...stops[index], id });
  return normalizeTour({ stops }).stops;
}

export function tourViewIsDirty(stop, viewport, viewportSize = null) {
  if (!stop) return false;
  const current = normalizeViewport(viewport);
  const saved = viewportForSize(stop.viewport, stop.viewport_size, viewportSize);
  if (!current || !saved) return false;
  return Math.abs(current.x - saved.x) > 0.5
    || Math.abs(current.y - saved.y) > 0.5
    || Math.abs(current.zoom - saved.zoom) > 0.001;
}

export function moveTourStop(tour, index, offset) {
  const stops = [...normalizeTour(tour).stops];
  const target = index + offset;
  if (index < 0 || index >= stops.length || target < 0 || target >= stops.length) return stops;
  [stops[index], stops[target]] = [stops[target], stops[index]];
  return stops;
}

export function reorderTourStop(tour, fromIndex, toIndex) {
  const stops = [...normalizeTour(tour).stops];
  if (fromIndex < 0 || fromIndex >= stops.length || toIndex < 0 || toIndex >= stops.length || fromIndex === toIndex) return stops;
  const [moved] = stops.splice(fromIndex, 1);
  stops.splice(toIndex, 0, moved);
  return stops;
}

export function removeTourStop(tour, index) {
  return normalizeTour(tour).stops.filter((_, stopIndex) => stopIndex !== index);
}

export function prefersReducedMotion() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function presentationKeyAction(key) {
  return ({ ArrowRight: "next", " ": "next", ArrowLeft: "previous", Home: "first", End: "last", Escape: "exit", f: "fullscreen", F: "fullscreen" })[key] || null;
}
